<template>
  <main-template>
    <v-container>
      <v-row>
        <v-col cols="12" class="d-flex align-center">
          <v-btn color="primary" x-small text @click="$router.go(-1)">
            <v-icon left>fa fa-angle-left</v-icon>
          </v-btn>
          <span class="text-h6 ml-2">Mensalidades para pagamento</span>
        </v-col>
      </v-row>

      <v-row justify="center">
        <v-col cols="12" sm="8" md="6">
          <div v-if="carregando" class="text-center py-8">
            <v-progress-circular indeterminate color="primary" />
          </div>

          <template v-else>
            <!-- Botão antecipar parcelas -->
            <div class="d-flex justify-end mb-3">
              <v-btn
                v-if="!modoAntecipar"
                text
                small
                color="primary"
                disabled
                @click="ativarModoAntecipar"
              >
                <v-icon left small>mdi-calendar-clock</v-icon>
                Antecipar mensalidades
              </v-btn>
              <v-btn v-else text small color="grey" @click="cancelarAntecipar"> Cancelar </v-btn>
            </div>

            <!-- Lista de parcelas -->
            <v-card outlined>
              <template v-for="(parcela, idx) in parcelasExibidas">
                <v-list-item :key="parcela.id" class="py-2" @click="toggleSelecionada(parcela)">
                  <v-list-item-action class="mr-2">
                    <v-checkbox
                      :input-value="estaSelecionada(parcela)"
                      color="primary"
                      hide-details
                      readonly
                    />
                  </v-list-item-action>

                  <v-list-item-avatar size="36" color="primary lighten-4 mr-2">
                    <v-icon color="primary">mdi-school</v-icon>
                  </v-list-item-avatar>

                  <v-list-item-content>
                    <v-list-item-title class="text-body-2 font-weight-medium">
                      {{ parcela.descricaoMensalidade }}
                    </v-list-item-title>
                    <v-list-item-subtitle v-if="parcela.aluno" class="text-caption">
                      {{ parcela.aluno.nomecompleto }}
                    </v-list-item-subtitle>
                    <div
                      v-for="(tier, ti) in parcela.descontosTiers"
                      :key="`tier-${parcela.id}-${ti}`"
                      class="text-caption grey--text"
                    >
                      Desconto de {{ tier.valor | currency }} até
                      {{ tier.data | dateParse("YYYY-MM-DD") | dateFormat("DD/MM/YYYY") }}
                    </div>
                    <div class="d-flex flex-wrap mt-1">
                      <v-chip v-if="parcela.estaAtrasada" x-small color="error" class="mr-1 mb-1">
                        Atrasada
                      </v-chip>
                      <v-chip v-else x-small color="info" class="mr-1 mb-1">A vencer</v-chip>
                      <v-chip v-if="parcela.temDesconto" x-small color="success" class="mr-1 mb-1">
                        Com desconto
                      </v-chip>
                    </div>
                    <div class="d-flex align-center mt-1">
                      <span
                        v-if="parcela.temDesconto"
                        class="text-caption grey--text mr-2"
                        style="text-decoration: line-through"
                      >
                        {{ parcela.valor | currency }}
                      </span>
                      <span class="text-body-2 font-weight-bold">
                        {{ parcela.valorEmAberto | currency }}
                      </span>
                      <v-spacer />
                      <router-link
                        :to="{
                          name: 'matriculas.mensalidades.detalhe',
                          params: {
                            parcela_id: parcela.id,
                            matricula_id: matriculaId,
                          },
                        }"
                        class="text-caption primary--text"
                        @click.stop.native
                      >
                        Ver detalhes &rarr;
                      </router-link>
                    </div>
                  </v-list-item-content>
                </v-list-item>
                <v-divider v-if="idx < parcelasExibidas.length - 1" :key="`div-${parcela.id}`" />
              </template>
            </v-card>
          </template>

          <!-- Bottom bar sticky -->
          <v-card class="bottom-bar-sticky mt-4 pa-4" elevation="8">
            <div class="d-flex align-center mb-2">
              <span class="text-caption grey--text">
                {{ selecionadas.length }} item(s) selecionado(s)
              </span>
              <v-spacer />
              <router-link
                v-if="selecionadas.length === 1"
                :to="{
                  name: 'matriculas.mensalidades.detalhe',
                  params: {
                    parcela_id: selecionadas[0].id,
                    matricula_id: matriculaId,
                  },
                }"
                class="text-caption primary--text mr-3"
              >
                Ver detalhes &rarr;
              </router-link>
              <span class="text-body-2 font-weight-bold">
                {{ totalSelecionadas | currency }}
              </span>
            </div>
            <v-btn
              color="primary"
              block
              :disabled="selecionadas.length === 0"
              @click="irParaFormaPagamento"
            >
              Pagar
            </v-btn>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </main-template>
</template>

<script>
import { mapGetters } from "vuex";
import Parcela from "@/Models/Parcela";

export default {
  name: "MensalidadeItensFatura",

  computed: {
    ...mapGetters("Mensalidades", ["matriculaSelecionada", "parcelasSelecionadas"]),

    matriculaId() {
      return this.$route.params.matricula_id;
    },

    parcelasEmAberto() {
      if (!this.matriculaSelecionada?.parcelas) return [];
      const aluno = this.matriculaSelecionada.aluno;
      return this.matriculaSelecionada.parcelas
        .map((p) => new Parcela({ ...p, aluno }))
        .filter((p) => p.estaEmAberto)
        .sort((a, b) => a.numero - b.numero);
    },

    parcelasExibidas() {
      if (this.modoAntecipar) return this.parcelasEmAberto;
      // Modo normal: exibe apenas a parcela pré-selecionada
      return this.selecionadas.length > 0
        ? [this.selecionadas[0]]
        : this.parcelasEmAberto.slice(0, 1);
    },

    totalSelecionadas() {
      return this.selecionadas.reduce((acc, p) => acc + parseFloat(p.valorEmAberto), 0);
    },
  },

  data() {
    return {
      carregando: false,
      modoAntecipar: false,
      selecionadas: [],
    };
  },

  mounted() {
    // Inicializa com as parcelas já selecionadas do Vuex (vindas da home)
    if (this.parcelasSelecionadas && this.parcelasSelecionadas.length > 0) {
      this.selecionadas = [this.parcelasSelecionadas[0]];
    } else {
      // Fallback: pré-seleciona a primeira em aberto
      this.carregarEPreselecionarPrimeira();
    }
  },

  methods: {
    async carregarEPreselecionarPrimeira() {
      if (!this.matriculaSelecionada) {
        this.carregando = true;
        try {
          const matricula = await this.$services.matriculasService.pegarMatricula(this.matriculaId);
          const matriculaDetalhada = await this.$services.matriculasService.getDetails(
            matricula.id
          );
          this.$store.commit("Mensalidades/setMatriculaSelecionada", matriculaDetalhada);
        } catch (error) {
          this.$handleError(error);
        } finally {
          this.carregando = false;
        }
      }
      // Pré-seleciona a primeira parcela em aberto
      const primeira = this.parcelasEmAberto[0];
      if (primeira) {
        this.selecionadas = [primeira];
      }
    },

    ativarModoAntecipar() {
      this.modoAntecipar = true;
    },

    cancelarAntecipar() {
      this.modoAntecipar = false;
      const primeira = this.parcelasEmAberto[0];
      this.selecionadas = primeira ? [primeira] : [];
    },

    estaSelecionada(parcela) {
      return this.selecionadas.some((s) => s.id === parcela.id);
    },

    toggleSelecionada(parcela) {
      if (this.estaSelecionada(parcela)) {
        this.selecionadas = [];
      } else {
        this.selecionadas = [parcela];
      }
    },

    irParaFormaPagamento() {
      this.$store.commit("Mensalidades/setParcelasSelecionadas", this.selecionadas);
      this.$router.push({
        name: "matriculas.mensalidades.formaPagamento",
        params: { matricula_id: this.matriculaId, parcelaId: this.selecionadas[0].id },
      });
    },
  },
};
</script>

<style scoped>
.bottom-bar-sticky {
  position: sticky;
  bottom: 0;
  z-index: 2;
}
</style>
